import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const ReportingManager = () => {
    const [reportingManagers, setReportingManagers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('active'); 
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [reportingManagerToDelete, setReportingManagerToDelete] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingReportingManagerId, setEditingReportingManagerId] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [employeeInput, setEmployeeInput] = useState('');
    const [employeeNotFound, setEmployeeNotFound] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        status: 'active',
        employeeId: ''
    });
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        fetchReportingManagers();
        fetchEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.currentPage, search, statusFilter]);
    const fetchEmployees = async () => {
        if (!API_URL) return;

        try {
            const payload = {
                action: 'get-active-employees-list'
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                const list = result.employees || result.data || [];
                if (Array.isArray(list)) {
                    // Normalize to { id, name }
                    const normalized = list.map((emp) => ({
                        id: emp.userId || emp.id,
                        name: emp.fullName || emp.name || ''
                    })).filter(emp => emp.id && emp.name);
                    setEmployees(normalized);
                } else {
                    setEmployees([]);
                }
            } else {
                setEmployees([]);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };


    const fetchReportingManagers = async () => {
        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: 'get-reporting-managers',
                page: pagination.currentPage,
                limit: pagination.limit,
                search: search || '',
                ...(statusFilter !== 'all' && { status: statusFilter })
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                setReportingManagers(result.data || []);
                if (result.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        currentPage: result.pagination.currentPage || prev.currentPage,
                        totalPages: result.pagination.totalPages || prev.totalPages,
                        totalCount: result.pagination.total || prev.totalCount,
                        hasNextPage: result.pagination.hasNextPage || false,
                        hasPreviousPage: result.pagination.hasPreviousPage || false
                    }));
                }
            } else {
                toast.error(result?.message || 'Failed to fetch reporting managers');
            }
        } catch (error) {
            console.error('Error fetching reporting managers:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleStatusFilter = (e) => {
        const value = e.target.value;
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const getSerialNumber = (index) => {
        return (pagination.currentPage - 1) * pagination.limit + index + 1;
    };

    const getStatusBadge = (status) => {
        const statusLower = status?.toLowerCase();
        let className = 'employee-status';
        let text = status?.toUpperCase() || 'ACTIVE';

        switch (statusLower) {
            case 'active':
                className += ' employee-status-active';
                text = 'ACTIVE';
                break;
            case 'inactive':
                className += ' employee-status-inactive';
                text = 'INACTIVE';
                break;
            default:
                className += ' employee-status-active';
                text = 'ACTIVE';
        }

        return <span className={className}>{text}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const handleAdd = () => {
        setIsEditMode(false);
        setEditingReportingManagerId(null);
        setFormData({
            name: '',
            status: 'active',
            employeeId: ''
        });
        setEmployeeInput('');
        setEmployeeNotFound(false);
        setFormErrors({});
        setShowAddModal(true);
    };

    const handleEdit = (reportingManager) => {
        setIsEditMode(true);
        setEditingReportingManagerId(reportingManager.id);
        setFormData({
            name: reportingManager.name || '',
            status: reportingManager.status || 'active',
            employeeId: reportingManager.employeeId || ''
        });
        setFormErrors({});
        setShowAddModal(true);
    };

    const handleDelete = (reportingManager) => {
        setReportingManagerToDelete(reportingManager);
        setShowDeleteModal(true);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setIsEditMode(false);
        setEditingReportingManagerId(null);
        setFormData({
            name: '',
            status: 'active',
            employeeId: ''
        });
        setEmployeeInput('');
        setEmployeeNotFound(false);
        setFormErrors({});
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setReportingManagerToDelete(null);
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name || !formData.name.trim()) {
            errors.name = 'Reporting Manager name is required';
        } else if (formData.name.trim().length < 2) {
            errors.name = 'Reporting Manager name must be at least 2 characters';
        }

        if (!isEditMode && (!formData.employeeId || !formData.employeeId.trim())) {
            errors.employeeId = 'Please select an employee';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            const currentUserId = localStorage.getItem('id');

            if (isEditMode) {
                // Update reporting manager
                const payload = {
                    action: 'update-reporting-manager',
                    id: editingReportingManagerId,
                    name: formData.name.trim(),
                    status: formData.status || 'active',
                    ...(currentUserId && { updatedBy: currentUserId })
                };

                const response = await axios.post(API_URL, payload, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                const result = response.data;

                if (result?.success) {
                    toast.success('Updated successfully');
                    closeAddModal();
                    fetchReportingManagers();
                } else {
                    toast.error(result?.message || 'Failed to update reporting manager');
                }
            } else {
                // Add reporting manager
                const payload = {
                    action: 'add-reporting-manager',
                    name: formData.name.trim(),
                    status: 'active',
                    userId: formData.employeeId, // pass selected employee's userId to API
                    ...(currentUserId && { createdBy: currentUserId })
                };

                const response = await axios.post(API_URL, payload, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                const result = response.data;

                if (result?.success) {
                    // Mark the selected employee as reporting manager
                    if (formData.employeeId) {
                        try {
                            const setResp = await axios.post(API_URL, {
                                action: 'set-reporting-manager',
                                id: formData.employeeId,
                                isReportingManager: true
                            }, {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            });
                            const setResult = setResp.data;
                            if (!setResult?.success) {
                                toast.error(setResult?.message || 'Failed to mark as reporting manager');
                            }
                        } catch (err) {
                            console.error('Error setting reporting manager flag:', err);
                            toast.error('Failed to mark as reporting manager');
                        }
                    }

                    toast.success('Created successfully');
                    closeAddModal();
                    fetchReportingManagers();
                } else {
                    toast.error(result?.message || 'Failed to create reporting manager');
                }
            }
        } catch (error) {
            console.error('Error saving reporting manager:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);

            // Handle specific error cases
            if (error.response?.status === 409) {
                setFormErrors({ name: 'Reporting Manager name already exists' });
            }
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!reportingManagerToDelete) return;

        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        const updatedBy = localStorage.getItem('id');

        setLoading(true);
        try {
            const payload = {
                action: 'delete-reporting-manager',
                id: reportingManagerToDelete.id,
                ...(updatedBy && { updatedBy: updatedBy })
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                toast.success('Deleted');
                // Unset reporting manager flag for the associated employee (if available)
                const employeeIdForUnset = reportingManagerToDelete?.employeeId || reportingManagerToDelete?.userId;
                if (employeeIdForUnset) {
                    try {
                        const unsetResp = await axios.post(API_URL, {
                            action: 'set-reporting-manager',
                            id: employeeIdForUnset,
                            isReportingManager: false
                        }, {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        });
                        const unsetResult = unsetResp.data;
                        if (!unsetResult?.success) {
                            toast.error(unsetResult?.message || 'Failed to unset reporting manager flag');
                        }
                    } catch (err) {
                        console.error('Error unsetting reporting manager flag:', err);
                        toast.error('Failed to unset reporting manager flag');
                    }
                } else {
                    console.warn('No employeeId found to unset reporting manager flag for deleted record');
                }
                closeDeleteModal();
                fetchReportingManagers();
            } else {
                toast.error(result?.message || 'Failed to delete reporting manager');
            }
        } catch (error) {
            console.error('Error deleting reporting manager:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    {/* Header with Title and Add Button */}
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-8">
                            <h1 className="employee-title">
                                Manage Reporting Managers
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                Manage reporting managers for your organization by adding, updating, or deleting reporting manager entries.
                            </p>
                        </div>
                        <div className="col-md-4 employee-view-header-actions">
                            <button type="button" className="employee-back-btn" onClick={handleAdd}>
                                Add Reporting Manager
                            </button>
                        </div>
                    </div>

                    {/* Reporting Manager Table Card */}
                    <div className="card employee-content-card">
                        <div className="card-body">
                            <div className="employee-table-container">
                                <div className="employee-search-container" style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', flexWrap: 'wrap' }}>
                                    <div className="form-group" style={{ marginBottom: 0, flex: '1', minWidth: '200px' }}>
                                        <input
                                            type="text"
                                            className="form-input employee-search-input"
                                            placeholder="Search reporting managers..."
                                            value={search}
                                            onChange={handleSearch}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label htmlFor="statusFilter" className="form-label" style={{ marginBottom: '5px', fontSize: '12px', color: '#6b7280', display: 'block' }}>
                                            Status
                                        </label>
                                        <select
                                            id="statusFilter"
                                            className="form-input"
                                            value={statusFilter}
                                            onChange={handleStatusFilter}
                                            style={{
                                                padding: '8px 12px',
                                                fontSize: '14px',
                                                height: '40px',
                                                width: '150px'
                                            }}
                                        >
                                            <option value="all">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>

                                {loading ? (
                                    <Loading size={120} />
                                ) : (
                                    <>
                                        <div className="employee-table-wrapper">
                                            <table className="employee-table">
                                                <thead>
                                                    <tr>
                                                        <th>S.No</th>
                                                        <th>Reporting Manager Name</th>
                                                        <th>Status</th>
                                                        <th>Created At</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportingManagers.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="5" className="employee-empty">
                                                                No reporting managers found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        reportingManagers.map((reportingManager, index) => (
                                                            <tr key={reportingManager.id}>
                                                                <td>{getSerialNumber(index)}</td>
                                                                <td>{reportingManager.name || '-'}</td>
                                                                <td>{getStatusBadge(reportingManager.status)}</td>
                                                                <td>{formatDate(reportingManager.createdAt)}</td>
                                                                <td>
                                                                    <div className="employee-actions">
                                                                        <button
                                                                            className="employee-action-btn employee-action-edit"
                                                                            onClick={() => handleEdit(reportingManager)}
                                                                            title="Edit"
                                                                        >
                                                                            <i className="fa-solid fa-pencil"></i>
                                                                        </button>
                                                                        <button
                                                                            className="employee-action-btn employee-action-delete"
                                                                            onClick={() => handleDelete(reportingManager)}
                                                                            title="Delete"
                                                                        >
                                                                            <i className="fa-solid fa-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {reportingManagers.length > 0 && pagination.totalPages > 0 && (
                                            <div className="employee-pagination">
                                                <button
                                                    className="pagination-btn"
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={pagination.currentPage === 1}
                                                >
                                                    «
                                                </button>
                                                <button
                                                    className="pagination-btn"
                                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                    disabled={pagination.currentPage === 1}
                                                >
                                                    ‹
                                                </button>
                                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                                    <button
                                                        key={page}
                                                        className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                                                        onClick={() => handlePageChange(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                ))}
                                                <button
                                                    className="pagination-btn"
                                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                    disabled={pagination.currentPage === pagination.totalPages}
                                                >
                                                    ›
                                                </button>
                                                <button
                                                    className="pagination-btn"
                                                    onClick={() => handlePageChange(pagination.totalPages)}
                                                    disabled={pagination.currentPage === pagination.totalPages}
                                                >
                                                    »
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Add/Edit Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={closeAddModal}>
                    <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{isEditMode ? 'Edit Reporting Manager' : 'Add Reporting Manager'}</h3>
                            <button className="modal-close" onClick={closeAddModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                {!isEditMode && (
                                    <div className="form-group">
                                        <label htmlFor="employeeInput" className="form-label">
                                            Select Employee <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="employeeInput"
                                            list="employee-list"
                                            className={`form-input ${formErrors.employeeId ? 'error' : ''}`}
                                            placeholder="Start typing employee name"
                                            value={employeeInput}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setEmployeeInput(value);
                                                const match = employees.find(emp => emp.name.toLowerCase() === value.toLowerCase());
                                                if (match) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        employeeId: match.id,
                                                        name: match.name
                                                    }));
                                                    setEmployeeNotFound(false);
                                                    if (formErrors.employeeId) {
                                                        setFormErrors(prev => ({ ...prev, employeeId: '' }));
                                                    }
                                                } else {
                                                    setFormData(prev => ({ ...prev, employeeId: '', name: '' }));
                                                    setEmployeeNotFound(!!value.trim());
                                                }
                                            }}
                                            required
                                        />
                                        <datalist id="employee-list">
                                            {employees.map(emp => (
                                                <option key={emp.id} value={emp.name} />
                                            ))}
                                        </datalist>
                                        {employeeNotFound && (
                                            <span className="error-message">Name not found</span>
                                        )}
                                        {formErrors.employeeId && (
                                            <span className="error-message">{formErrors.employeeId}</span>
                                        )}
                                    </div>
                                )}

                                {/* Reporting Manager Name removed; we rely on selected employee */}

                                {isEditMode && (
                                    <div className="form-group">
                                        <label htmlFor="status" className="form-label">
                                            Status <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="status"
                                            className={`form-input ${formErrors.status ? 'error' : ''}`}
                                            value={formData.status}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, status: e.target.value }));
                                                if (formErrors.status) {
                                                    setFormErrors(prev => ({ ...prev, status: '' }));
                                                }
                                            }}
                                            required
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                        {formErrors.status && (
                                            <span className="error-message">{formErrors.status}</span>
                                        )}
                                    </div>
                                )}

                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeAddModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update' : 'Add')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={closeDeleteModal}>
                    <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                            <button className="modal-close" onClick={closeDeleteModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete <strong>{reportingManagerToDelete?.name} Reporting Manager</strong>?</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-primary" onClick={confirmDelete}>
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportingManager;
